import React, { useMemo } from "react";
import { useQuery } from "urql";
import { round } from "lodash";

import { Grid, Column } from "../../../../components/src";
import { FoNavInstrumentName } from "./FoNavInstrumentName";
import { GET_FONAV_FUND_NAV } from "./queries";
import { foNavNumberFormat } from "./utils";

const navFormat = foNavNumberFormat("# ##0");

interface FoNavFundNavProps {
    fundId: string;
    startDate: string;
}

export function FoNavFundNav({ fundId, startDate }: FoNavFundNavProps): React.ReactElement {
    const [{ fetching, error, data }] = useQuery({
        query: GET_FONAV_FUND_NAV,
        variables: {
            fundId,
            date: startDate
        },
        pause: !fundId || !startDate,
        requestPolicy: "network-only"
    });

    const nav = data?.previousNav?.[0];

    const classRows = useMemo(() => {
        if (!nav?.data?.classes) {
            return [];
        }

        return nav.data.classes.map((fundClass) => ({
            name: fundClass.name,
            instrumentId: fundClass.instrumentId,
            navAfterAdjustmentsAfterOrders: navFormat(round(Number(fundClass.navAfterAdjustmentsAfterOrders) * Number(fundClass.fxRate), 0)),
            navCurrency: fundClass.navCurrency
        }));
    }, [nav]);

    if (fetching) {
        return <p>Loading fund NAV...</p>;
    }

    if (error) {
        return <p>error: {JSON.stringify(error, null, 2)}</p>;
    }

    if (!nav) {
        return <p>No saved NAV found on or before {startDate}.</p>;
    }

    return (
        <Grid header="Fund classes" data={classRows} className="table-timeseries" tableClassName="table-xs" sortable>
            <Column
                field="name"
                title="Class"
                className="nowrap left"
                format={(value: string, item: { instrumentId?: string }) => (
                    <FoNavInstrumentName name={value} instrumentId={item?.instrumentId} />
                )}
            />
            <Column field="navAfterAdjustmentsAfterOrders" title="NAV after orders after fee" className="nowrap left" />
            <Column field="navCurrency" title="Currency" />
        </Grid>
    );
}
